/**
 * The month's job review, print-first: /accounting/statement/YYYY-MM?view=jobs
 * (admin) — the "Job review" tab beside the statement. With ?print=1 it opens
 * the print dialog once laid out ("Download PDF" is Save as PDF from there).
 *
 * Every job INVOICED in the month, one row each, with Finalize's margin; the
 * figures come from GET /accounting/statements/:month/jobs (lib/jobReview.ts) —
 * the stored review for a closed month, live figures otherwise. Nothing is
 * computed here except laying it out.
 *
 * LETTER PAGES, LAID OUT LIKE THE STATEMENT: fixed row heights (a job's row is
 * one line plus its note lines), pages filled by arithmetic, the table heading
 * repeated on each page, the slim header (Page X of N) and the footer. At phone
 * width the screen shows the same figures as cards instead; print always uses
 * the pages. Colours are the CRM theme's (--crm-navy, the amber tokens).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useApiClient } from '../hooks/useApi';
import { formatMoney, formatDate, formatDateTime } from '../lib/utils';

const NAVY = 'var(--crm-navy)';
const AMBER = 'var(--crm-amber-fill)';
const AMBER_TINT = 'var(--crm-amber-tint)';
const AMBER_LINE = 'var(--crm-amber-soft-line)';
const AMBER_TEXT = 'var(--crm-amber)';
const INK = 'var(--crm-text)';
const MUTED = 'var(--crm-muted)';
const RULE = 'var(--crm-line)';
const RED = 'var(--crm-red)';
const ON_NAVY = 'var(--crm-on-fill)';
const ON_NAVY_SOFT = 'var(--crm-rail-hover)';
const PAPER = 'var(--crm-panel)';
const DESK = 'var(--crm-panel-2)';
const MONO = "'DM Mono', ui-monospace, monospace";

// ---- Geometry (CSS px; letter = 816 × 1056 at 96 dpi) — the statement's -------
const PAGE_W = 816, PAGE_H = 1056, MARGIN = 40;
const SLIM = 26, SLIM_GAP = 10, FOOT = 22;
const BAND = 104, BAND_GAP = 14, TILES = 70, TILES_GAP = 16;
const CONTENT_H = PAGE_H - 2 * MARGIN;
const CAP_FIRST = CONTENT_H - SLIM - SLIM_GAP - BAND - BAND_GAP - TILES - TILES_GAP - FOOT;
const CAP_REST = CONTENT_H - SLIM - SLIM_GAP - FOOT;
const H = { thead: 26, line: 19, note: 13, total: 24, foot: 14, gap: 12, rhead: 26, rline: 18, rsum: 20 };
const COLS = '44px minmax(0, 1fr) 72px 70px 66px 68px 74px 46px 62px';
const NOTE_CHARS = 140; // a note line at 9px across the table

// ---- Blocks -------------------------------------------------------------------
type Block =
  | { t: 'row'; row: any; lines: { text: string; flag?: boolean }[] }
  | { t: 'total' }
  | { t: 'foot'; text: string }
  | { t: 'gap' }
  | { t: 'rhead'; label: string }
  | { t: 'rline'; label: string; amount: number }
  | { t: 'rsum'; label: string; amount: number };

/** The note pieces packed into lines of about NOTE_CHARS; flags on lines of their own. */
function noteLines(row: any): { text: string; flag?: boolean }[] {
  const out: { text: string; flag?: boolean }[] = [];
  let cur = '';
  for (const piece of row.notes as string[]) {
    if (cur && cur.length + 3 + piece.length > NOTE_CHARS) { out.push({ text: cur }); cur = piece; }
    else cur = cur ? `${cur} · ${piece}` : piece;
  }
  if (cur) out.push({ text: cur });
  for (const f of row.flags as string[]) out.push({ text: `Check: ${f}`, flag: true });
  return out;
}

const blockH = (b: Block) =>
  b.t === 'row' ? H.line + b.lines.length * H.note
    : b.t === 'total' ? H.total : b.t === 'foot' ? H.foot : b.t === 'gap' ? H.gap
      : b.t === 'rhead' ? H.rhead : b.t === 'rline' ? H.rline : H.rsum;
const inTable = (b: Block) => b.t === 'row' || b.t === 'total';

function buildBlocks(r: any): Block[] {
  const rec = r.reconciliation;
  const blocks: Block[] = r.rows.map((row: any) => ({ t: 'row', row, lines: noteLines(row) }));
  blocks.push({ t: 'total' });
  blocks.push({ t: 'gap' });
  blocks.push({ t: 'foot', text: 'Margin before tax = invoiced (before tax) − contractor portion − commission − adjustments. Margin % = margin ÷ invoiced.' });
  blocks.push({ t: 'foot', text: 'Every figure is the job\'s Finalize margin. Adjustments = card processing (net) + Accounting adjustments on the job (claims, chargebacks).' });
  blocks.push({ t: 'gap' });
  for (const [label, x] of [['Revenue', rec.revenue], ['Contractor pay', rec.contractor], ['Commission', rec.commission]] as const) {
    blocks.push({ t: 'rhead', label: `Reconciles to the Monthly View — ${label.toLowerCase()}` });
    for (const l of x.lines) blocks.push({ t: 'rline', label: l.label, amount: l.amount });
    blocks.push({ t: 'rsum', label: `Monthly View ${label.toLowerCase()}`, amount: x.monthlyView });
    blocks.push({ t: 'gap' });
  }
  return blocks;
}

/** Pages of blocks. A table page starts with the column heading; a reconciliation heading keeps its first line. */
function paginate(blocks: Block[]): Block[][] {
  const pages: Block[][] = [[]];
  let used = 0;
  let tableOpen = false;
  const cap = () => (pages.length === 1 ? CAP_FIRST : CAP_REST);
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    if (b.t === 'gap' && used === 0) continue;
    const head = inTable(b) && !tableOpen ? H.thead : 0;
    const keep = b.t === 'rhead' && blocks[i + 1] ? blockH(blocks[i + 1]) : 0;
    if (used > 0 && used + head + blockH(b) + keep > cap()) {
      pages.push([]); used = 0; tableOpen = false;
      if (b.t === 'gap') continue;
    }
    if (inTable(b) && !tableOpen) { used += H.thead; tableOpen = true; }
    if (!inTable(b)) tableOpen = false;
    pages[pages.length - 1].push(b);
    used += blockH(b);
  }
  return pages;
}

// ---- Rendering ----------------------------------------------------------------
const clip: React.CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 };
const bb: React.CSSProperties = { boxSizing: 'border-box' };
const money = (v: number | null | undefined) => (v == null ? '—' : formatMoney(v));
const pct = (v: number | null | undefined) => (v == null ? '—' : `${v.toFixed(1)}%`);

const HEADS = ['Job #', 'Client', 'Invoiced', 'Contractor', 'Commission', 'Adjust.', 'Margin $', 'Margin %', 'Taxes'];

function TableHead({ continued }: { continued: boolean }) {
  return (
    <div style={{ ...bb, height: H.thead, display: 'grid', gridTemplateColumns: COLS, gap: 6, alignItems: 'center', padding: '0 8px', background: NAVY, color: ON_NAVY, fontWeight: 700, fontSize: 10 }}>
      {HEADS.map((h, k) => (
        <span key={h} style={{ ...clip, textAlign: k >= 2 ? 'right' : 'left' }}>
          {h}{k === 1 && continued ? <span style={{ fontWeight: 400, opacity: 0.8 }}> (continued)</span> : null}
        </span>
      ))}
    </div>
  );
}

function Figures({ x, bold }: { x: any; bold?: boolean }) {
  const cell = (v: React.ReactNode, extra?: React.CSSProperties) => <span style={{ ...clip, textAlign: 'right', fontFamily: MONO, ...extra }}>{v}</span>;
  return (
    <>
      {cell(money(x.invoiced))}
      {cell(money(x.contractor))}
      {cell(money(x.commission))}
      {cell(money(x.adjustments))}
      {cell(money(x.margin), { background: AMBER_TINT, padding: '0 3px', color: x.margin < 0 ? RED : INK, fontWeight: bold ? 700 : 600 })}
      {cell(pct(x.marginPct), { color: (x.marginPct ?? 0) < 0 ? RED : INK })}
      {cell(money(x.taxes))}
    </>
  );
}

function BlockView({ b, r }: { b: Block; r: any }) {
  if (b.t === 'row') {
    const row = b.row;
    return (
      <div style={{ ...bb, height: blockH(b), borderBottom: `1px solid ${RULE}`, padding: '0 8px' }}>
        <div style={{ height: H.line, display: 'grid', gridTemplateColumns: COLS, gap: 6, alignItems: 'center', fontSize: 10.5, color: INK }}>
          <span style={{ ...clip, fontWeight: 600 }}>{row.jobNo}</span>
          <span style={clip}>{row.client}</span>
          <Figures x={row} />
        </div>
        {b.lines.map((l, k) => (
          <div key={k} style={{ height: H.note, lineHeight: `${H.note}px`, fontSize: 9, color: l.flag ? AMBER_TEXT : MUTED, fontWeight: l.flag ? 600 : 400, paddingLeft: 50, ...clip }}>{l.text}</div>
        ))}
      </div>
    );
  }
  if (b.t === 'total') {
    return (
      <div style={{ ...bb, height: H.total, display: 'grid', gridTemplateColumns: COLS, gap: 6, alignItems: 'center', padding: '0 8px', fontSize: 10.5, fontWeight: 700, color: NAVY, borderTop: `2px solid ${NAVY}`, borderBottom: `2px solid ${NAVY}` }}>
        <span>Total</span>
        <span style={clip}>{r.rows.length} job{r.rows.length === 1 ? '' : 's'}</span>
        <Figures x={{ ...r.totals }} bold />
      </div>
    );
  }
  if (b.t === 'foot') return <div style={{ ...bb, height: H.foot, lineHeight: `${H.foot}px`, padding: '0 8px', fontSize: 9.5, color: MUTED, fontStyle: 'italic', ...clip }}>{b.text}</div>;
  if (b.t === 'gap') return <div style={{ height: H.gap }} />;
  if (b.t === 'rhead') {
    return <div style={{ ...bb, height: H.rhead, display: 'flex', alignItems: 'center', padding: '0 8px', background: NAVY, color: ON_NAVY, fontWeight: 700, fontSize: 11.5, ...clip }}>{b.label}</div>;
  }
  return (
    <div style={{ ...bb, height: blockH(b), display: 'flex', alignItems: 'center', gap: 8, padding: `0 8px 0 ${b.t === 'rline' ? 20 : 8}px`, fontSize: b.t === 'rsum' ? 11 : 10.5, fontWeight: b.t === 'rsum' ? 700 : 400, color: INK, borderBottom: `1px solid ${b.t === 'rsum' ? NAVY : RULE}` }}>
      <span style={{ ...clip, flex: 1 }}>{b.t === 'rsum' ? `= ${b.label}` : b.label}</span>
      <span style={{ fontFamily: MONO, color: b.amount < 0 ? RED : undefined }}>{money(b.amount)}</span>
    </div>
  );
}

function Tile({ label, value, sub, amber }: { label: string; value: string; sub?: string; amber?: boolean }) {
  return (
    <div style={{ flex: 1, minWidth: 0, height: TILES, boxSizing: 'border-box', padding: '9px 10px', border: `1px solid ${amber ? AMBER_LINE : RULE}`, background: amber ? AMBER_TINT : PAPER, borderRadius: 6 }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: amber ? AMBER_TEXT : MUTED, textTransform: 'uppercase', letterSpacing: 0.4, ...clip }}>{label}</div>
      <div style={{ marginTop: 6, fontSize: 15, fontWeight: 700, fontFamily: MONO, color: NAVY, ...clip }}>{value}</div>
      {sub && <div style={{ fontSize: 9.5, fontWeight: 700, color: amber ? AMBER_TEXT : MUTED, ...clip }}>{sub}</div>}
    </div>
  );
}

function tiles(r: any) {
  const t = r.totals;
  return [
    { label: 'Invoiced (before tax)', value: money(t.invoiced) },
    { label: 'Contractor', value: money(t.contractor) },
    { label: 'Commission', value: money(t.commission) },
    { label: 'Adjustments', value: money(t.adjustments) },
    { label: 'Margin before tax', value: money(t.margin), sub: pct(t.marginPct), amber: true },
    { label: 'Taxes', value: money(t.taxes) },
  ];
}

/** "Statement | Job review" — the two reports for one month. */
export function ReportTabs({ month, active }: { month: string; active: 'statement' | 'jobs' }) {
  const tab = (key: 'statement' | 'jobs', label: string, href: string) => (
    <a href={href} aria-current={active === key ? 'page' : undefined}
      style={{ padding: '6px 12px', fontSize: 13, fontWeight: 600, textDecoration: 'none', color: active === key ? ON_NAVY : NAVY, background: active === key ? NAVY : PAPER }}>
      {label}
    </a>
  );
  return (
    <div style={{ display: 'inline-flex', border: `1px solid ${NAVY}`, borderRadius: 7, overflow: 'hidden' }}>
      {tab('statement', 'Statement', `/accounting/statement/${month}`)}
      {tab('jobs', 'Job review', `/accounting/statement/${month}?view=jobs`)}
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  width: PAGE_W, height: PAGE_H, padding: MARGIN, boxSizing: 'border-box', position: 'relative', overflow: 'hidden',
  background: PAPER, color: INK, fontFamily: "Inter, 'Helvetica Neue', Arial, sans-serif",
};

/** Phone width: the same figures as cards. */
function Cards({ r, badge }: { r: any; badge: string }) {
  const kv = (label: string, v: string, strong?: boolean) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13, padding: '2px 0' }}>
      <span style={{ color: MUTED }}>{label}</span><span style={{ fontFamily: MONO, fontWeight: strong ? 700 : 400 }}>{v}</span>
    </div>
  );
  const rec = r.reconciliation;
  return (
    <div className="jr-cards" style={{ padding: '0 16px 24px', color: INK, fontFamily: "Inter, 'Helvetica Neue', Arial, sans-serif" }}>
      <div style={{ background: NAVY, borderRadius: 8, padding: '14px 16px', marginBottom: 12 }}>
        <div style={{ color: AMBER, fontSize: 10.5, fontWeight: 700, letterSpacing: 1.4, textTransform: 'uppercase' }}>Job review</div>
        <div style={{ color: ON_NAVY, fontSize: 22, fontWeight: 700, marginTop: 2 }}>{r.title}</div>
        <div style={{ color: ON_NAVY_SOFT, fontSize: 12, marginTop: 2 }}>{r.rows.length} job{r.rows.length === 1 ? '' : 's'} invoiced · {badge}</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
        {tiles(r).map((t) => <Tile key={t.label} {...t} />)}
      </div>
      {r.rows.length === 0 && <div style={{ color: MUTED, fontSize: 13, fontStyle: 'italic' }}>No jobs were invoiced this month.</div>}
      {r.rows.map((row: any) => (
        <div key={row.leadId} style={{ background: PAPER, border: `1px solid ${RULE}`, borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
            <b style={{ fontSize: 14 }}>{row.jobNo}</b>
            <span style={{ ...clip, flex: 1, fontSize: 14 }}>{row.client}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', margin: '6px 0', padding: '6px 8px', background: AMBER_TINT, borderRadius: 6, fontWeight: 700 }}>
            <span style={{ color: AMBER_TEXT, fontSize: 12 }}>Margin before tax</span>
            <span style={{ fontFamily: MONO, color: row.margin < 0 ? RED : NAVY }}>{money(row.margin)} · {pct(row.marginPct)}</span>
          </div>
          {kv('Invoiced (before tax)', money(row.invoiced))}
          {kv('Contractor portion', money(row.contractor))}
          {kv('Commission', money(row.commission))}
          {kv('Adjustments', money(row.adjustments))}
          {kv('Taxes', money(row.taxes))}
          <div style={{ marginTop: 6, fontSize: 11.5, color: MUTED }}>{(row.notes as string[]).join(' · ')}</div>
          {(row.flags as string[]).map((f) => <div key={f} style={{ marginTop: 4, fontSize: 11.5, color: AMBER_TEXT, fontWeight: 600 }}>Check: {f}</div>)}
        </div>
      ))}
      <div style={{ background: PAPER, border: `2px solid ${NAVY}`, borderRadius: 8, padding: '10px 12px', margin: '12px 0' }}>
        <b style={{ color: NAVY }}>Totals · {r.rows.length} job{r.rows.length === 1 ? '' : 's'}</b>
        {kv('Invoiced (before tax)', money(r.totals.invoiced), true)}
        {kv('Contractor portion', money(r.totals.contractor), true)}
        {kv('Commission', money(r.totals.commission), true)}
        {kv('Adjustments', money(r.totals.adjustments), true)}
        {kv('Margin before tax', `${money(r.totals.margin)} · ${pct(r.totals.marginPct)}`, true)}
        {kv('Taxes', money(r.totals.taxes), true)}
      </div>
      <div style={{ fontSize: 11.5, color: MUTED, fontStyle: 'italic', marginBottom: 12 }}>
        Margin before tax = invoiced (before tax) − contractor portion − commission − adjustments. Margin % = margin ÷ invoiced.
        Every figure is the job's Finalize margin; adjustments are card processing (net) plus Accounting adjustments on the job.
      </div>
      {[['Revenue', rec.revenue], ['Contractor pay', rec.contractor], ['Commission', rec.commission]].map(([label, x]: any) => (
        <div key={label} style={{ background: PAPER, border: `1px solid ${RULE}`, borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
          <b style={{ color: NAVY, fontSize: 13 }}>Reconciles to the Monthly View — {label.toLowerCase()}</b>
          {x.lines.map((l: any) => kv(l.label, money(l.amount)))}
          {kv(`= Monthly View ${label.toLowerCase()}`, money(x.monthlyView), true)}
        </div>
      ))}
    </div>
  );
}

export function JobReviewPrint({ month, autoPrint }: { month: string; autoPrint: boolean }) {
  const api = useApiClient();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.getJobReview>>['data'] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api.getJobReview(month).then((res) => { if (alive) setData(res.data ?? null); }).catch((e: any) => { if (alive) setError(e?.message || 'Could not load the job review'); });
    return () => { alive = false; };
  }, [api, month]);

  const r = data?.review;
  const pages = useMemo(() => (r ? paginate(buildBlocks(r)) : null), [r]);
  const locked = !!data?.closed && !data?.closedWithout;

  useEffect(() => {
    if (!r) return;
    document.title = `Job review ${r.title}${locked ? '' : ' (draft)'} – BVL CRM`;
    if (!autoPrint) return;
    const go = () => setTimeout(() => window.print(), 250);
    (document as any).fonts?.ready ? (document as any).fonts.ready.then(go) : go();
  }, [r, autoPrint]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <div style={{ padding: 40, fontFamily: 'sans-serif', color: RED }}>{error}</div>;
  if (!r || !pages) return <div style={{ padding: 40, fontFamily: 'sans-serif', color: MUTED }}>Loading the job review…</div>;

  const closed = data?.closed;
  const footer = locked
    ? `Closed by ${closed!.byName} on ${formatDate(String(closed!.at).slice(0, 10))}`
    : `Draft — live figures as of ${formatDateTime(r.generatedAt)}. Not closed.`;
  const badge = locked ? 'CLOSED · LOCKED' : 'DRAFT · LIVE FIGURES';
  const changes = data?.changes ?? [];

  // Which table page is a continuation: any page after the one the table started on.
  let tableSeen = false;

  return (
    <div className="statement-doc">
      <style>{`
        @page { size: letter; margin: 0; }
        html, body { margin: 0; background: ${DESK}; }
        .statement-doc { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .statement-page { margin: 0 auto 24px; box-shadow: 0 2px 10px var(--crm-shadow-color, rgba(16,24,40,.15)); }
        .jr-cards { display: none; }
        @media screen and (max-width: 840px) {
          .jr-pages { display: none; }
          .jr-cards { display: block; }
        }
        @media print {
          html, body { background: ${PAPER}; }
          .no-print, .jr-cards { display: none !important; }
          .jr-pages { display: block !important; }
          .statement-page { margin: 0; box-shadow: none; break-after: page; page-break-after: always; }
          .statement-page:last-child { break-after: auto; page-break-after: auto; }
        }
      `}</style>

      <div className="no-print" style={{ maxWidth: PAGE_W, margin: '0 auto', padding: '16px', boxSizing: 'border-box', fontFamily: 'sans-serif' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <a href="/accounting" style={{ color: NAVY, fontSize: 13 }}>← Accounting</a>
          <ReportTabs month={month} active="jobs" />
          <div style={{ flex: 1 }} />
          <button onClick={() => window.print()} style={{ padding: '7px 14px', background: NAVY, color: ON_NAVY, border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>Print / Save as PDF</button>
        </div>
        {data?.closedWithout && (
          <div style={{ marginTop: 12, padding: '10px 12px', background: AMBER_TINT, border: `1px solid ${AMBER_LINE}`, borderRadius: 8, fontSize: 12.5, color: AMBER_TEXT }}>
            <b>Live figures.</b> {r.title} was closed before the job review existed, so none was stored. Reopen and close the month again to lock one.
          </div>
        )}
        {locked && changes.length > 0 && (
          <div style={{ marginTop: 12, padding: '10px 12px', background: AMBER_TINT, border: `1px solid ${AMBER_LINE}`, borderRadius: 8, fontSize: 12.5, color: AMBER_TEXT }}>
            <b>Changed since close.</b> This review shows the figures as closed; the live figures now differ:
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {changes.map((c) => <li key={c.label}>{c.label}: {c.label.endsWith('jobs') ? c.closed : formatMoney(c.closed)} → {c.label.endsWith('jobs') ? c.live : formatMoney(c.live)}</li>)}
            </ul>
          </div>
        )}
      </div>

      <Cards r={r} badge={badge} />

      <div className="jr-pages">
        {pages.map((page, pi) => {
          const firstTable = page.findIndex(inTable);
          const continued = firstTable >= 0 && tableSeen;
          if (firstTable >= 0) tableSeen = true;
          return (
            <div key={pi} className="statement-page" style={pageStyle}>
              <div style={{ ...bb, height: SLIM, marginBottom: SLIM_GAP, display: 'flex', alignItems: 'center', borderBottom: `1px solid ${RULE}`, fontSize: 10, color: MUTED }}>
                <span style={{ flex: 1 }}><b style={{ color: NAVY }}>{r.title}</b> · Job review</span>
                <span>Page {pi + 1} of {pages.length}</span>
              </div>
              {pi === 0 && (
                <>
                  <div style={{ height: BAND, marginBottom: BAND_GAP, boxSizing: 'border-box', background: NAVY, borderRadius: 6, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: AMBER, fontSize: 10.5, fontWeight: 700, letterSpacing: 1.6, textTransform: 'uppercase' }}>Job review · jobs invoiced</div>
                      <div style={{ color: ON_NAVY, fontSize: 28, fontWeight: 700, marginTop: 4 }}>{r.title}</div>
                      <div style={{ color: ON_NAVY_SOFT, fontSize: 11, marginTop: 2 }}>Baltic Van Lines · {formatDate(r.from)} – {formatDate(r.to)} · {r.rows.length} job{r.rows.length === 1 ? '' : 's'}</div>
                    </div>
                    <img src="/brand/logo-horizontal-notag-white.svg" alt="Baltic Van Lines" style={{ height: 28, width: 'auto', display: 'block', flexShrink: 0 }} />
                    <div style={{ border: `2px solid ${locked ? AMBER : ON_NAVY_SOFT}`, color: locked ? AMBER : ON_NAVY, borderRadius: 5, padding: '6px 10px', fontSize: 11, fontWeight: 700, letterSpacing: 1.2, whiteSpace: 'nowrap', transform: 'rotate(-3deg)' }}>
                      {badge}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: TILES_GAP }}>
                    {tiles(r).map((t) => <Tile key={t.label} {...t} />)}
                  </div>
                  {r.rows.length === 0 && <div style={{ padding: '0 8px', fontSize: 10.5, color: MUTED, fontStyle: 'italic', height: H.line }}>No jobs were invoiced this month.</div>}
                </>
              )}
              {page.map((b, k) => (
                <React.Fragment key={k}>
                  {k === firstTable && <TableHead continued={continued} />}
                  <BlockView b={b} r={r} />
                </React.Fragment>
              ))}
              <div style={{ ...bb, position: 'absolute', left: MARGIN, right: MARGIN, bottom: MARGIN, height: FOOT, display: 'flex', alignItems: 'flex-end', borderTop: `1px solid ${RULE}`, fontSize: 9.5, color: MUTED }}>
                <span style={{ flex: 1 }}>{footer}</span>
                <span>Baltic Van Lines</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
